import { WizardStatus as Wizard_Status_Enum } from '@risksmart-app/domain/src/types/consts/wizard-status';
import type { GetWizardByIdResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetWizardByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetWizardByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetWizardByIdArgs = {
  riskId: string;
};

// The generated `GetWizardByIdQuery` marks `CurrentStep` and `Status` as
// NON-null (Hasura's column metadata), but the underlying `wizard` columns are
// nullable, so the tRPC row types them `number | null` / `WizardStatus | null`.
// Reconcile the codegen drift here: `Wizard_Status_Enum` shares identical
// values with `WizardStatus`, and the consumer null-guards both fields anyway
// (`?? 0`, `?? null`), so coalescing to the generated shape is behaviour-safe.
const toGraphQLRow = (row: GetWizardByIdResponseRow) => ({
  RiskId: row.RiskId,
  CurrentStep: row.CurrentStep ?? 0,
  AssessmentId: row.AssessmentId,
  ActivityId: row.ActivityId,
  Status: row.Status ?? Wizard_Status_Enum.Planned,
});

export const useGetWizardById = createQueryHook<
  UseGetWizardByIdArgs,
  GetWizardByIdResponseRow | null,
  GetWizardByIdQuery
>({
  trpcQueryOptions: (trpc, { riskId }) =>
    trpc.frontend.wizard.wizardById.queryOptions({ riskId }),
  // The legacy query root is the `wizard` array of 0..1 (consumers read
  // `data.wizard[0]`); the tRPC procedure returns the unwrapped row | null, so
  // wrap it back into the array shape the consumer expects.
  mapTrpcDataToGraphQL: (data) => ({
    wizard: data ? [toGraphQLRow(data)] : [],
  }),
  graphqlDocument: GetWizardByIdDocument,
  graphqlVariables: ({ riskId }) => ({ RiskId: riskId }),
  // The initiator re-reads on mount via the skip toggle, so cache-first is
  // safe and avoids the no-cache console.warn promoted to FATAL in vitest.
  graphqlFetchPolicy: 'cache-first',
});
